/**
 * Le poche regole che non si possono indovinare guardando lo schermo.
 *
 * Deliberatamente NON è un manuale per schermata: quello invecchierebbe a ogni
 * rilascio e una guida che mente è peggio di nessuna guida. Qui stanno solo le
 * decisioni — come si arrotonda, cosa vuol dire competenza, come si somma un
 * avanzamento — che restano valide anche se l'interfaccia cambia.
 */
const REGOLE = [
  {
    titolo: 'Le ore diventano giornate con uno scarto di un\'ora',
    chi: 'Vale in economia, sui rendiconti',
    corpo: (
      <>
        <p>
          Se lavori almeno <strong>7 ore su 8</strong>, al cliente si fattura una giornata
          intera. Sotto quella soglia si fattura la frazione esatta, che nel documento
          compare in percentuale: 6,5 ore diventano <strong>“81% di una giornata”</strong>.
        </p>
        <p className="mt-2">
          Il <strong>compenso del consulente</strong> segue invece sempre le ore reali, senza
          arrotondamenti: 6,5 ore su una tariffa da 250 €/giornata valgono 203,13 €. La
          differenza fra i due calcoli è il margine dell'arrotondamento, e resta a Sinthema.
        </p>
        <p className="mt-2 text-gray-500">
          Nel documento che alleghi alla fattura il cliente non vede mai le ore: solo
          giornate e percentuali. Il consulente vede sempre le ore che ha svolto.
        </p>
      </>
    ),
  },
  {
    titolo: 'La fattura appartiene al mese di competenza, non a quello in cui la emetti',
    chi: 'Vale in Fatturazione',
    corpo: (
      <>
        <p>
          La rata di agosto emessa il <strong>1° settembre</strong> appartiene ad{' '}
          <strong>agosto</strong>. È il mese di competenza a decidere dove la fattura compare
          nel riepilogo e quando scade la rata successiva.
        </p>
        <p className="mt-2">
          Se non lo indichi viene dedotto: le emissioni fatte <strong>entro il giorno 10</strong>{' '}
          si attribuiscono al mese precedente. In tabella una competenza dedotta è in corsivo
          grigio con un <code>?</code>; la correggi dalla scheda fattura.
        </p>
        <p className="mt-2 text-gray-500">
          Senza questa distinzione una commessa mensile slitterebbe di un mese a ogni giro.
        </p>
      </>
    ),
  },
  {
    titolo: 'Nel SAL la percentuale è quanto avanzi ORA, non il totale',
    chi: 'Vale sulle commesse a corpo',
    corpo: (
      <>
        <p>
          Se una commessa è già al 50% e la stai chiudendo, scrivi <strong>50</strong>, non
          100. Il sistema somma da sé e ti mostra “da 50% a 100%” mentre digiti.
        </p>
        <p className="mt-2">
          L'importo da fatturare si propone di conseguenza: il 50% di una commessa da
          5.000 € vale 2.500 €. Resta correggibile in approvazione.
        </p>
        <p className="mt-2 text-gray-500">
          Un rendiconto sbagliato va corretto, non compensato con uno negativo.
        </p>
      </>
    ),
  },
  {
    titolo: 'A corpo o in economia: cambia da dove nasce l\'importo',
    chi: 'Vale su commesse e offerte consulente',
    corpo: (
      <>
        <p>
          <strong>A corpo</strong>: l'importo è fisso e concordato. Nel rendiconto dichiari
          una percentuale di avanzamento, e da quella nasce la fattura. Le ore non si
          rendicontano.
        </p>
        <p className="mt-2">
          <strong>In economia</strong>: si lavora a giornate o ore su un listino. L'importo
          nasce da ciò che rendiconti, più le eventuali spese riaddebitate. La percentuale
          di avanzamento qui non ha senso e infatti non compare.
        </p>
        <p className="mt-2 text-gray-500">
          Sulle offerte dei consulenti la stessa distinzione decide come si misura lo
          sforamento: a corpo sull'importo, in economia sulla più critica fra importo e
          giornate consumate.
        </p>
      </>
    ),
  },
  {
    titolo: 'Cosa vede — e cosa non vede — un consulente esterno',
    chi: 'Vale sul portale esterno',
    corpo: (
      <>
        <p>
          Il consulente vede le commesse su cui è assegnato, l'avanzamento, le proprie ore e
          giornate e il <strong>proprio compenso</strong>.
        </p>
        <p className="mt-2">
          Non vede <strong>nulla di ciò che è concordato col cliente</strong>: budget,
          fatturato, tariffe cliente, condizioni di pagamento. Non sono nascosti
          nell'interfaccia — non escono proprio dal server. Può inoltre aprire soltanto i
          rendiconti che ha creato lui.
        </p>
      </>
    ),
  },
];

export default function Guida() {
  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-2xl font-display font-semibold text-gray-900">Come funziona</h1>
        <p className="text-sm text-gray-500 mt-1">
          Non un manuale di ogni schermata — quello invecchierebbe a ogni modifica. Qui
          stanno le poche regole che il gestionale applica e che non si possono indovinare
          guardando i pulsanti.
        </p>
      </div>

      {REGOLE.map((r, i) => (
        <section key={i} className="card">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
            <h2 className="text-base font-semibold text-gray-900">{r.titolo}</h2>
            <span className="text-xs text-gray-400 shrink-0">{r.chi}</span>
          </div>
          <div className="text-sm text-gray-700 leading-relaxed">{r.corpo}</div>
        </section>
      ))}

      <p className="text-xs text-gray-400">
        Manca qualcosa o una regola non torna? Usa <strong>💬 Segnala un problema</strong> in
        fondo al menu: la schermata e la versione vengono allegate da sole.
      </p>
    </div>
  );
}
